import { useState, useEffect } from 'react';
import { CustomList, CreateListRequest, AddItemRequest, ListItem } from '../types/types';
import { logger } from '../lib/logger';

interface GuestListItem extends Omit<ListItem, 'vendor'> {
  vendor_id: number;
}

interface GuestList extends Omit<CustomList, 'items'> {
  items: GuestListItem[];
}

const STORAGE_KEY = 'ceen_guest_lists';

export const useGuestLists = () => {
  const [lists, setLists] = useState<GuestList[]>([]);

  // Load from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        setLists(JSON.parse(stored));
      }
    } catch (error) {
      logger.error('Error loading guest lists:', error);
    }
  }, []);

  // Save to localStorage whenever lists change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(lists));
    } catch (error) {
      logger.error('Error saving guest lists:', error);
    }
  }, [lists]);

  const createList = (data: CreateListRequest): GuestList => {
    const newList: GuestList = {
      id: Date.now(), // Temporary ID
      uuid: crypto.randomUUID(),
      title: data.title,
      description: data.description || '',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      items: [],
    };

    setLists(prev => [...prev, newList]);
    logger.log('Guest list created:', newList);
    return newList;
  };

  const addItemToList = (listId: number, itemData: AddItemRequest): GuestListItem => {
    const newItem: GuestListItem = {
      id: Date.now(),
      vendor_id: itemData.vendor_id,
      event_date: itemData.event_date,
      comment: itemData.comment || '',
      display_id: `${itemData.vendor_id}-${itemData.event_date}`,
      added_at: new Date().toISOString(),
    };

    setLists(prev =>
      prev.map(list => {
        if (list.id === listId) {
          // Check if already exists
          const exists = list.items.some(
            item => item.vendor_id === itemData.vendor_id && item.event_date === itemData.event_date
          );

          if (!exists) {
            return { ...list, items: [...list.items, newItem] };
          }
        }
        return list;
      })
    );

    logger.log('Item added to guest list:', newItem);
    return newItem;
  };

  const isEventPinned = (vendorId: number, eventDate: string): boolean => {
    return lists.some(list =>
      list.items.some(item => item.vendor_id === vendorId && item.event_date === eventDate)
    );
  };

  const clearAllLists = () => {
    setLists([]);
    localStorage.removeItem(STORAGE_KEY);
  };

  return {
    lists,
    createList,
    addItemToList,
    isEventPinned,
    clearAllLists,
  };
};
